import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const FavoritesContext = createContext(null);
const LS_KEY = "uk-favorites-v1";

const readLS = () => {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((x) => typeof x === "string") : [];
  } catch (e) { return []; }
};
const writeLS = (ids) => {
  try { localStorage.setItem(LS_KEY, JSON.stringify(ids)); } catch (e) { /* quota */ }
};

/**
 * FavoritesProvider — central wishlist state.
 *
 *  - Guests: IDs persisted in localStorage (LS_KEY = "uk-favorites-v1")
 *  - Logged-in users: IDs synced with backend via /api/favorites/ids
 *  - Login transition: existing guest IDs are POSTed to /api/favorites/sync,
 *    then merged with the server's set. Local cache is updated to match.
 *
 * Exposed API: { ids, count, isFavorite, toggle, add, remove, ready }
 */
export function FavoritesProvider({ children }) {
  const { user } = useAuth();
  const [ids, setIds] = useState(() => readLS());
  const [ready, setReady] = useState(false);
  const lastUserIdRef = useRef(null);
  // Incremented on every sync request — discards stale GET responses if the
  // user toggles a favorite mid-flight.
  const syncVersionRef = useRef(0);

  // Sync with server on auth change. We use lastUserIdRef to detect actual
  // transitions and avoid duplicate sync calls during HMR / re-renders.
  useEffect(() => {
    const currentUid = user?.id || null;
    if (currentUid === lastUserIdRef.current && ready) return;
    lastUserIdRef.current = currentUid;

    if (!user) {
      // Guest: re-read local cache (no network).
      setIds(readLS());
      setReady(true);
      return;
    }

    // Logged-in: sync guest favorites into account, then read canonical list back.
    const myVersion = ++syncVersionRef.current;
    (async () => {
      try {
        const localIds = readLS();
        if (localIds.length > 0) {
          await api.post("/favorites/sync", { product_ids: localIds });
        }
        const r = await api.get("/favorites/ids");
        // Drop result if the user toggled (or another sync started) in the meantime.
        if (myVersion !== syncVersionRef.current) return;
        const serverIds = Array.isArray(r.data) ? r.data : [];
        setIds(serverIds);
        writeLS(serverIds);
      } catch (e) {
        // Non-fatal — keep whatever we have locally.
      } finally {
        if (myVersion === syncVersionRef.current) setReady(true);
      }
    })();
  }, [user, ready]);

  // Persist guests' changes immediately (and mirror for authed users too).
  useEffect(() => {
    writeLS(ids);
  }, [ids]);

  const isFavorite = useCallback((pid) => ids.includes(pid), [ids]);

  // Any user action invalidates an in-flight login sync so the late GET
  // response cannot clobber the user's intent.
  const bumpVersion = () => { syncVersionRef.current += 1; };

  const add = useCallback(async (pid) => {
    if (!pid) return;
    bumpVersion();
    setIds((prev) => (prev.includes(pid) ? prev : [...prev, pid]));
    if (user) {
      try { await api.post(`/favorites/${pid}`); } catch (e) { /* keep optimistic */ }
    }
  }, [user]);

  const remove = useCallback(async (pid) => {
    if (!pid) return;
    bumpVersion();
    setIds((prev) => prev.filter((x) => x !== pid));
    if (user) {
      try { await api.delete(`/favorites/${pid}`); } catch (e) { /* keep optimistic */ }
    }
  }, [user]);

  const toggle = useCallback(async (pid) => {
    if (!pid) return;
    if (ids.includes(pid)) return remove(pid);
    return add(pid);
  }, [ids, add, remove]);

  const value = { ids, count: ids.length, isFavorite, toggle, add, remove, ready };
  return <FavoritesContext.Provider value={value}>{children}</FavoritesContext.Provider>;
}

export const useFavorites = () => {
  const ctx = useContext(FavoritesContext);
  if (!ctx) throw new Error("useFavorites must be used inside <FavoritesProvider>");
  return ctx;
};
